/**
 * The dashboard shell (spec §5.4, §5.5, §7.1–7.2; card D1): header (title, state badge, Close with its key glyph),
 * the fixed tab rail (numbers 1–8), the section strip, the body (Overview composed by the shell, sections as
 * list + detail + context) and the hint footer. Renders a snapshot only; every key goes through the controller.
 * Colours, type and spacing come from the `--ui-*` tokens (F1).
 */
import { useEffect, useRef, type ReactElement, type RefObject } from 'react'
import type { DashboardConfig } from './config'
import type { DashNavPort } from './focus'
import { OverviewBody, OverviewHeader } from './tabs/overview/Overview'
import type { OverviewData } from './tabs/overview/model'
import { DASH_TABS, dashSections, type DashCommandPort, type DashContext, type DashSection, type DashTabId } from './registry'

export interface DashboardProps {
  readonly cfg: DashboardConfig
  readonly tab: number
  readonly section: number
  readonly depth: number
  readonly focus: string | undefined
  readonly time: 'pause' | 'keepRunning'
  readonly ctx: DashContext
  readonly badges: Readonly<Record<string, number>>
  readonly port: DashCommandPort
  readonly nav: DashNavPort
  /** The Overview cards and header readouts (W4 C1); absent or null = no data yet. */
  readonly overview?: OverviewData | null
  /** Mouse: a tab, a section, Close. */
  onTab(index: number): void
  onSection(index: number): void
}

function tabBadge(sections: readonly DashSection[], badges: Readonly<Record<string, number>>): number {
  let n = 0
  for (const s of sections) n += badges[s.id] ?? 0
  return n
}

/** A hint line is "<keys> <action>" ("Q/E tab"): the keys render as a chip so Q reads as Q, not 0. */
function Hint({ line }: { line: string }): ReactElement {
  const cut = line.indexOf(' ')
  if (cut <= 0) return <span className="dash-hint">{line}</span>
  return (
    <span className="dash-hint">
      <kbd className="dash-hint__key">{line.slice(0, cut)}</kbd> {line.slice(cut + 1)}
    </span>
  )
}

/** Overflow up to this many pixels is rounding (fractional layout at UI scale), not more to read. */
const MORE_BELOW_SLACK_PX = 2

/** True while part of a scrolled box is still below its bottom edge (whole pixels; scrollTop can be fractional). */
export function hasMoreBelow(scrollTop: number, clientHeight: number, scrollHeight: number): boolean {
  return Math.ceil(scrollTop) + clientHeight + MORE_BELOW_SLACK_PX < scrollHeight
}

/**
 * The body's scroll cue (W4 polish): sets `data-more` on the scrolling body while more of it is below, so the CSS
 * fades its last row and puts a chevron on the footer rule (as the menu's long lists do). Re-checked on scroll, on
 * resize and when a section adds or removes rows; the attribute is set directly, so nothing re-renders.
 */
function useScrollCue(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current
    if (el === null) return undefined
    const update = (): void => {
      el.toggleAttribute('data-more', hasMoreBelow(el.scrollTop, el.clientHeight, el.scrollHeight))
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    resized?.observe(el)
    const changed = typeof MutationObserver === 'undefined' ? null : new MutationObserver(update)
    changed?.observe(el, { childList: true, subtree: true })
    return (): void => {
      el.removeEventListener('scroll', update)
      resized?.disconnect()
      changed?.disconnect()
    }
  }, [ref])
}

function Empty({ line, why }: { line: string; why?: string }): ReactElement {
  return (
    <div className="dash-empty" data-dash-empty="">
      <p className="dash-empty__line">{line}</p>
      {why !== undefined ? <p className="dash-empty__why">{why}</p> : null}
    </div>
  )
}

/** The empty tab's second line (Overview never shows one). */
function whyFor(cfg: DashboardConfig, tab: DashTabId): string | undefined {
  return tab === 'overview' ? undefined : cfg.comingSoonWhy[tab]
}

function SectionBody(p: DashboardProps & { sections: readonly DashSection[] }): ReactElement {
  const s = p.sections[Math.min(p.section, p.sections.length - 1)]
  if (s === undefined) return <Empty line={p.cfg.comingSoon} why={whyFor(p.cfg, DASH_TABS[p.tab])} />
  if (!s.available(p.ctx)) return <Empty line={p.cfg.unavailable} />
  const Body = s.component
  return (
    <div className="dash-body" data-dash-body={s.id} data-dash-depth={p.depth}>
      <Body ctx={p.ctx} focus={p.focus} port={p.port} nav={p.nav} />
    </div>
  )
}

export function Dashboard(p: DashboardProps): ReactElement {
  const { cfg } = p
  const tabId = DASH_TABS[p.tab]
  const sections = p.tab === 0 ? [] : dashSections(tabId, p.ctx.docked)
  const paused = p.time === 'pause'
  const h = cfg.hints
  const main = useRef<HTMLElement>(null)
  useScrollCue(main)
  const hints = p.tab === 0 ? [h.tab, h.jump, h.close, h.menu] : [h.open, h.section, h.tab, h.jump, h.back, h.close, h.menu]
  return (
    <div className="dash ui-panel" role="dialog" aria-label={cfg.title} data-dash-tab={tabId}>
      <header className="dash-header">
        <h2 className="dash-header__title">
          <span className="dash-header__game">{cfg.gameTitle}</span> · {cfg.title}
        </h2>
        <OverviewHeader header={p.overview?.view.header ?? null} />
        <span className={paused ? 'dash-badge dash-badge--paused' : 'dash-badge dash-badge--live'} data-dash-time={p.time}>
          {paused ? cfg.badges.paused : cfg.badges.live}
        </span>
        <button type="button" className="dash-close" data-dash-close="" onClick={() => p.port.close(true)}>
          {cfg.close} <kbd>{cfg.closeKey}</kbd>
        </button>
      </header>
      <nav className="dash-rail" role="tablist">
        <kbd className="dash-rail__key">Q</kbd>
        {DASH_TABS.map((id, i) => {
          const t = cfg.tabs[id]
          const n = i === 0 ? 0 : tabBadge(dashSections(id, p.ctx.docked), p.badges)
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={i === p.tab}
              className={i === p.tab ? 'dash-tab dash-tab--active' : 'dash-tab'}
              data-dash-tab-button={id}
              onClick={() => p.onTab(i)}
            >
              <span className="dash-tab__num">{i + 1}</span>
              <span className="dash-tab__label">{t.label}</span>
              <span className="dash-tab__short">{t.short}</span>
              {n > 0 ? <span className="dash-count">{n}</span> : null}
            </button>
          )
        })}
        <kbd className="dash-rail__key">E</kbd>
      </nav>
      {sections.length > 0 ? (
        <nav className="dash-sections">
          <kbd className="dash-rail__key">Z</kbd>
          {sections.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={i === p.section ? 'dash-section dash-section--active' : 'dash-section'}
              aria-current={i === p.section ? 'true' : undefined}
              data-dash-section={s.id}
              onClick={() => p.onSection(i)}
            >
              {s.label}
              {(p.badges[s.id] ?? 0) > 0 ? <span className="dash-count">{p.badges[s.id]}</span> : null}
            </button>
          ))}
          <kbd className="dash-rail__key">C</kbd>
        </nav>
      ) : null}
      <main ref={main} className="dash-main">{p.tab === 0 ? <OverviewBody data={p.overview ?? null} port={p.port} badges={p.badges} /> : <SectionBody {...p} sections={sections} />}</main>
      <footer className="dash-hints">
        {hints.map((line) => (
          <Hint key={line} line={line} />
        ))}
      </footer>
    </div>
  )
}
